import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Copy,
  Download,
  Settings2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { legacyRoutes } from '@/lib/legacy';
import { useAutoLocateFilteredPage } from '@/shared/hooks/useAutoLocateFilteredPage';
import { getAccountSites } from '../api';
import type { SiteDetail } from '../types';

interface SitesTabProps {
  accountId: string;
}

interface ColumnDef<T> {
  key: string;
  label: string;
  defaultVisible?: boolean;
  sortKey?: string;
  filterType?: 'text' | 'date-range' | 'select' | 'number-range';
  filterOptions?: { label: string; value: string }[];
  getValue: (item: T) => string | number | null;
  render?: (item: T) => ReactNode;
  align?: 'left' | 'center' | 'right';
  noFilter?: boolean;
  className?: string;
}

const PAGE_SIZE = 10;
const FILTER_STORAGE_KEY = 'atek-account-dashboard-sites-column-filters';
const VISIBILITY_STORAGE_KEY = 'atek-account-dashboard-sites-column-visibility';

/**
 * City / province / country, skipping whatever the site is missing. Same shape as
 * features/sites' formatAddress, minus the street line the column has no room for.
 */
function formatLocation(site: SiteDetail): string {
  return [site.city, site.stateProvince, site.country].filter(Boolean).join(', ');
}

const SITE_COLUMNS: ColumnDef<SiteDetail>[] = [
  {
    key: 'name',
    label: 'Name',
    sortKey: 'name',
    filterType: 'text',
    getValue: (site) => site.name,
    render: (site) => <span className="font-medium">{site.name}</span>,
  },
  {
    key: 'status',
    label: 'Status',
    sortKey: 'status',
    filterType: 'select',
    filterOptions: [
      { label: 'Active', value: 'active' },
      { label: 'Inactive', value: 'inactive' },
      { label: 'Pending', value: 'pending' },
    ],
    getValue: (site) => site.status,
    render: (site) => {
      const statusVariantMap: Record<string, 'success' | 'secondary' | 'warning'> = {
        active: 'success',
        inactive: 'secondary',
        pending: 'warning',
      };
      const statusVariant = statusVariantMap[site.status] || 'secondary';
      return <Badge variant={statusVariant}>{site.status}</Badge>;
    },
  },
  {
    key: 'location',
    label: 'Location',
    sortKey: 'location',
    filterType: 'text',
    // Shared between getValue and render so the sort/filter key can never drift
    // from what the cell displays.
    getValue: (site) => formatLocation(site),
    render: (site) => <span>{formatLocation(site) || '—'}</span>,
  },
  {
    key: 'timezone',
    label: 'Timezone',
    sortKey: 'timezone',
    filterType: 'text',
    getValue: (site) => site.timezone || '',
    render: (site) => (
      <span className="text-muted-foreground text-sm">{site.timezone || '—'}</span>
    ),
  },
  {
    key: 'sensorCount',
    label: 'Sensors',
    sortKey: 'sensorCount',
    filterType: 'number-range',
    getValue: (site) => (site.sensorCount != null ? site.sensorCount : 0),
    render: (site) => <span>{site.sensorCount ?? '—'}</span>,
    align: 'right',
    className: 'tabular-nums',
  },
  {
    key: 'actions',
    label: '',
    noFilter: true,
    getValue: () => '',
    render: (site) =>
      site.mongoSiteId ? (
        <a href={legacyRoutes.site(site.mongoSiteId)} className="text-atek-blue hover:underline text-sm">
          View
        </a>
      ) : null,
    align: 'right',
  },
];

function getDefaultVisibility(): Record<string, boolean> {
  const defaults: Record<string, boolean> = {};
  for (const col of SITE_COLUMNS) {
    defaults[col.key] = col.defaultVisible !== false;
  }
  return defaults;
}

function loadColumnVisibility(): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(VISIBILITY_STORAGE_KEY);
    if (stored) return { ...getDefaultVisibility(), ...JSON.parse(stored) };
  } catch {
    // Ignore malformed localStorage
  }
  return getDefaultVisibility();
}

function saveColumnVisibility(visibility: Record<string, boolean>) {
  localStorage.setItem(VISIBILITY_STORAGE_KEY, JSON.stringify(visibility));
}

function escapeCSVValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  let str = String(value);
  if (/^[=+\-@\t\r]/.test(str)) str = `'${str}`;
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function exportToCSV(items: SiteDetail[], columns: ColumnDef<SiteDetail>[], filename: string): void {
  const exportCols = columns.filter((c) => !c.noFilter);
  const header = exportCols.map((c) => escapeCSVValue(c.label)).join(',');
  const rows = items.map((item) =>
    exportCols.map((c) => escapeCSVValue(c.getValue(item))).join(','),
  );
  const csvContent = [header, ...rows].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function copyToClipboard(items: SiteDetail[], columns: ColumnDef<SiteDetail>[]): Promise<void> {
  const exportCols = columns.filter((c) => !c.noFilter);
  const header = exportCols.map((c) => c.label).join('\t');
  const rows = items.map((item) =>
    exportCols.map((c) => String(c.getValue(item) ?? '')).join('\t'),
  );
  return navigator.clipboard.writeText([header, ...rows].join('\n'));
}

function SortableHeader({
  label,
  sortKey,
  currentSortKey,
  sortDirection,
  onSort,
}: {
  label: string;
  sortKey: string;
  currentSortKey: string;
  sortDirection: 1 | -1;
  onSort: (key: string) => void;
}) {
  const isActive = currentSortKey === sortKey;

  return (
    <Button variant="ghost" size="sm" className="-ml-3 h-8 hover:bg-transparent" onClick={() => onSort(sortKey)}>
      {label}
      {isActive ? (
        sortDirection === 1 ? (
          <ArrowUp className="ml-1 h-3 w-3" />
        ) : (
          <ArrowDown className="ml-1 h-3 w-3" />
        )
      ) : (
        <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />
      )}
    </Button>
  );
}

function renderColumnFilter(
  col: ColumnDef<SiteDetail>,
  columnFilters: Record<string, string>,
  setColumnFilter: (key: string, value: string) => void,
) {
  if (col.noFilter) return <TableHead key={`filter-${col.key}`} />;

  if (col.filterType === 'select' && col.filterOptions) {
    const selected: string[] = (() => {
      try {
        return columnFilters[col.key] ? JSON.parse(columnFilters[col.key]) : [];
      } catch {
        return [];
      }
    })();

    const toggle = (opt: string) => {
      const next = selected.includes(opt)
        ? selected.filter((o) => o !== opt)
        : [...selected, opt];
      setColumnFilter(col.key, next.length ? JSON.stringify(next) : '');
    };

    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-7 w-full text-xs justify-start font-normal">
              {selected.length ? `${selected.length} selected` : '—'}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-44">
            {col.filterOptions.map((opt) => (
              <DropdownMenuCheckboxItem
                key={opt.value}
                checked={selected.includes(opt.value)}
                onCheckedChange={() => toggle(opt.value)}
                onSelect={(e) => e.preventDefault()}
              >
                {opt.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableHead>
    );
  }

  if (col.filterType === 'number-range') {
    const raw = columnFilters[col.key] || '';
    const [min = '', max = ''] = raw.split('>>');
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <div className="flex flex-col gap-0.5">
          <Input
            type="number"
            className="h-6 text-xs px-1"
            value={min}
            onChange={(e) => setColumnFilter(col.key, `${e.target.value}>>${max}`)}
            placeholder="Min"
          />
          <Input
            type="number"
            className="h-6 text-xs px-1"
            value={max}
            onChange={(e) => setColumnFilter(col.key, `${min}>>${e.target.value}`)}
            placeholder="Max"
          />
        </div>
      </TableHead>
    );
  }

  return (
    <TableHead key={`filter-${col.key}`} className="p-1">
      <Input
        className="h-7 text-xs"
        value={columnFilters[col.key] || ''}
        onChange={(e) => setColumnFilter(col.key, e.target.value)}
        placeholder="..."
      />
    </TableHead>
  );
}

export default function SitesTab({ accountId }: SitesTabProps) {
  const [page, setPage] = useState(0);
  const [sortKey, setSortKey] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<1 | -1>(1);

  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(() => {
    try {
      const stored = localStorage.getItem(FILTER_STORAGE_KEY);
      if (stored) return JSON.parse(stored);
    } catch {
      // Ignore malformed localStorage
    }
    return {};
  });

  useEffect(() => {
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(columnFilters));
  }, [columnFilters]);

  const setColumnFilter = (key: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
  };

  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(loadColumnVisibility);
  const toggleColumn = (key: string) => {
    const updated = { ...columnVisibility, [key]: !columnVisibility[key] };
    setColumnVisibility(updated);
    saveColumnVisibility(updated);
  };
  const isVisible = (key: string) => columnVisibility[key] !== false;
  const visibleColumns = SITE_COLUMNS.filter((col) => isVisible(col.key));

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['account-sites', accountId, page],
    queryFn: () =>
      getAccountSites(accountId, {
        pageNumber: page,
        pageLength: PAGE_SIZE,
      }),
  });

  const sites = data?.data || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / PAGE_SIZE) || 1;

  const filteredSites = useMemo(() => {
    const activeFilters = Object.entries(columnFilters).filter(([, value]) => value);
    if (activeFilters.length === 0) return sites;

    return sites.filter((site) =>
      activeFilters.every(([colKey, filterValue]) => {
        const col = SITE_COLUMNS.find((c) => c.key === colKey);
        if (!col || col.noFilter) return true;

        const rawValue = col.getValue(site);

        if (col.filterType === 'select') {
          try {
            const selected = JSON.parse(filterValue) as string[];
            return selected.length === 0 || selected.includes(String(rawValue || ''));
          } catch {
            return true;
          }
        }

        if (col.filterType === 'number-range') {
          const [minStr, maxStr] = filterValue.split('>>');
          const num = Number(rawValue);
          if (Number.isNaN(num)) return false;
          if (minStr && num < Number(minStr)) return false;
          if (maxStr && num > Number(maxStr)) return false;
          return true;
        }

        return String(rawValue ?? '')
          .toLowerCase()
          .includes(filterValue.toLowerCase());
      }),
    );
  }, [sites, columnFilters]);

  const hasActiveColumnFilters = useMemo(
    () => Object.values(columnFilters).some(Boolean),
    [columnFilters],
  );

  useAutoLocateFilteredPage({
    hasActiveFilters: hasActiveColumnFilters,
    filterSignature: JSON.stringify(columnFilters),
    filteredCount: filteredSites.length,
    pageItemCount: sites.length,
    page,
    totalPages,
    isLoading,
    onPageChange: setPage,
  });

  const sortedSites = useMemo(() => {
    const col = SITE_COLUMNS.find((c) => c.sortKey === sortKey);
    if (!col) return filteredSites;

    return [...filteredSites].sort((a, b) => {
      const aVal = col.getValue(a);
      const bVal = col.getValue(b);

      const aNum = Number(aVal);
      const bNum = Number(bVal);
      if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
        return sortDirection === 1 ? aNum - bNum : bNum - aNum;
      }

      const aStr = String(aVal ?? '').toLowerCase();
      const bStr = String(bVal ?? '').toLowerCase();
      const cmp = aStr.localeCompare(bStr);
      return sortDirection === 1 ? cmp : -cmp;
    });
  }, [filteredSites, sortDirection, sortKey]);

  const handleSort = (nextSortKey: string) => {
    if (sortKey === nextSortKey) {
      setSortDirection((prev) => (prev === 1 ? -1 : 1));
    } else {
      setSortKey(nextSortKey);
      setSortDirection(1);
    }
  };

  const handleCopy = async () => {
    await copyToClipboard(sortedSites, SITE_COLUMNS);
    toast.success('Copied to clipboard');
  };

  const handleExportCSV = () => {
    exportToCSV(sortedSites, SITE_COLUMNS, 'account-sites');
    toast.success(`Exported ${sortedSites.length} rows`);
  };

  if (isLoading) {
    return <SitesTabSkeleton />;
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-10 text-center space-y-4">
          <p className="text-destructive">Error loading sites</p>
          <p className="text-sm text-muted-foreground">
            {error instanceof Error ? error.message : 'An unexpected error occurred'}
          </p>
          <Button variant="outline" onClick={() => refetch()}>
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Sites</CardTitle>
            <CardDescription>
              {filteredSites.length < sites.length
                ? `Showing ${filteredSites.length} of ${sites.length} on this page`
                : `Showing ${sites.length} of ${total}`}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleCopy}>
              <Copy className="h-4 w-4 mr-1" />
              Copy
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportCSV}>
              <Download className="h-4 w-4 mr-1" />
              CSV
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Settings2 className="h-4 w-4 mr-1" />
                  Columns
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {SITE_COLUMNS.filter((c) => !c.noFilter).map((col) => (
                  <DropdownMenuCheckboxItem
                    key={col.key}
                    checked={isVisible(col.key)}
                    onCheckedChange={() => toggleColumn(col.key)}
                    onSelect={(e) => e.preventDefault()}
                  >
                    {col.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/30 hover:bg-muted/30">
                {visibleColumns.map((col) =>
                  renderColumnFilter(col, columnFilters, setColumnFilter),
                )}
              </TableRow>
              <TableRow>
                {visibleColumns.map((col) => (
                  <TableHead
                    key={col.key}
                    className={cn(
                      col.align === 'right' && 'text-right',
                      col.align === 'center' && 'text-center',
                    )}
                  >
                    {col.sortKey ? (
                      <SortableHeader
                        label={col.label}
                        sortKey={col.sortKey}
                        currentSortKey={sortKey}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                      />
                    ) : (
                      col.label
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedSites.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length} className="py-12 text-center text-muted-foreground">
                    No sites found
                  </TableCell>
                </TableRow>
              ) : (
                sortedSites.map((site) => (
                  <TableRow key={site.id} className="hover:bg-muted/50">
                    {visibleColumns.map((col) => (
                      <TableCell
                        key={`${site.id}-${col.key}`}
                        className={cn(
                          'text-sm',
                          col.align === 'right' && 'text-right tabular-nums',
                          col.align === 'center' && 'text-center',
                          col.className,
                        )}
                      >
                        {col.render ? col.render(site) : String(col.getValue(site) ?? '') || '—'}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {totalPages > 1 && (
          <div className="border-t px-4 py-3 flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Page {page + 1} of {totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SitesTabSkeleton() {
  return (
    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-4 w-24 mt-1" />
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
